import { useCallback, useMemo, useSyncExternalStore } from 'react';
import type { Agent, AgentReasoningConfig } from '@/types/agent.types';
import type { ReasoningToggleState } from './buildEffectiveReasoningEffort';

export type { ReasoningToggleState } from './buildEffectiveReasoningEffort';

export interface UseReasoningToggleResult {
  state: ReasoningToggleState;
  setEnabled: (enabled: boolean) => void;
  isVisible: boolean;
}

const STORAGE_KEY = 'stellar_reasoning_toggles';
type StoreMap = Record<string, ReasoningToggleState>;

function readStorage(): StoreMap {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as StoreMap) : {};
  } catch {
    return {};
  }
}

// Module-level mutable store. New object reference on every change so
// useSyncExternalStore re-renders subscribers. Persisted to localStorage
// on each write; subscribers are notified after persist.
let inMemory: StoreMap = readStorage();
const subscribers = new Set<() => void>();

function notify(): void {
  subscribers.forEach((cb) => cb());
}

function persist(): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(inMemory));
  } catch {
    // localStorage unavailable (private mode / quota). State stays in
    // memory; we deliberately swallow the error.
  }
}

function subscribe(cb: () => void): () => void {
  subscribers.add(cb);
  return () => {
    subscribers.delete(cb);
  };
}

function getSnapshot(): StoreMap {
  return inMemory;
}

function writeState(agentId: string, next: ReasoningToggleState): void {
  inMemory = {
    ...inMemory,
    [agentId]: next,
  };
  persist();
  notify();
}

/**
 * TESTING ONLY. Re-reads localStorage so each test starts fresh.
 * Do not call from production code.
 */
export function _resetStoreForTests(): void {
  inMemory = readStorage();
  notify();
}

/**
 * Resolve an agent's reasoning config, but only for Azure AI agents — other
 * frameworks have no reasoning concept and must never surface the toggle.
 */
function getReasoningConfig(
  agent: Agent | undefined
): AgentReasoningConfig | undefined {
  if (agent?.config.framework !== 'azure-ai-agent') return undefined;
  return agent.config.config.reasoningConfig;
}

export function useReasoningToggle(
  agentId: string | undefined,
  agent: Agent | undefined
): UseReasoningToggleResult {
  const map = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  const reasoning = getReasoningConfig(agent);
  const isVisible = !!reasoning?.supportsReasoning;

  const stored = agentId ? map[agentId] : undefined;
  const seedEnabled = reasoning?.defaultEnabled === true; // default OFF
  const state: ReasoningToggleState = useMemo(
    () => stored ?? { enabled: seedEnabled },
    [stored, seedEnabled]
  );

  const setEnabled = useCallback(
    (enabled: boolean) => {
      if (agentId) writeState(agentId, { enabled });
    },
    [agentId]
  );

  return { state, setEnabled, isVisible };
}
